import React, { useState } from "react";
import axios from "../../api/axios";

import { Grid, Paper, Typography, Button, Box } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

const categories = [
  { key: "appointment", label: "Appointment Loader" },
  { key: "bank", label: "Bank Loader" },
  { key: "pension", label: "Pension Loader" },
  { key: "id", label: "ID Loader" },
  { key: "stoppage", label: "Temporary Stoppage Loader" },
  { key: "custom", label: "Custom Loader" },
];

export default function VariationLoaders() {
  const [activeCategory, setActiveCategory] = useState(null);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [message, setMessage] = useState("");

  // Download template
  const handleDownloadTemplate = async () => {
    if (!activeCategory) return;
    try {
      const res = await axios.get(`/variations/template/${activeCategory}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${activeCategory}_loader_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      setMessage("❌ Failed to download template");
    }
  };

  // Select file
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Preview file (demo only)
  const handlePreview = () => {
    if (!file) {
      setMessage("⚠️ Please select a file first.");
      return;
    }

    setPreviewData([
      {
        pfno: "12345",
        surname: "Doe",
        firstname: "John",
        othername: "A.",
        ...(activeCategory === "bank" && {
          bank_name: "UBA",
          account_number: "0123456789",
        }),
      },
    ]);
    setMessage("✅ Preview generated. Confirm to upload.");
  };

  // Upload file
  const handleUpload = async () => {
    if (!file) {
      setMessage("⚠️ No file selected.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`/variations/upload/${activeCategory}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMessage("✅ Upload successful. Pending approval.");
      setFile(null);
      setPreviewData([]);
    } catch (error) {
      console.error(error.response?.data || error);
      setMessage("❌ Upload failed. Please try again.");
    }
  };

  // Close modal
  const handleClose = () => {
    setActiveCategory(null);
    setFile(null);
    setPreviewData([]);
    setMessage("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Grid container justifyContent="center" sx={{ p: 3 }}>
        <Grid item xs={12} md={10} lg={8}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Variation Loaders
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Choose a category below, download a template, and upload your data for approval.
            </Typography>

            {/* Category Cards */}
            <Grid container spacing={3} mt={1}>
              {categories.map((cat) => (
                <Grid item xs={12} sm={6} md={4} key={cat.key}>
                  <Paper
                    onClick={() => setActiveCategory(cat.key)}
                    elevation={3}
                    sx={{
                      p: 3,
                      textAlign: "center",
                      cursor: "pointer",
                      borderRadius: 3,
                      height: "120px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "0.3s",
                      "&:hover": {
                        backgroundColor: "primary.main",
                        color: "#fff",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Typography fontWeight="600">{cat.label}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Modal */}
            {activeCategory && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  bgcolor: "rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1300,
                }}
              >
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    width: "100%",
                    maxWidth: 650,
                    maxHeight: "90vh",
                    overflowY: "auto",
                    boxShadow: 6,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {categories.find((c) => c.key === activeCategory)?.label}
                  </Typography>

                  {/* Buttons row */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
                    <Button variant="contained" color="primary" onClick={handleDownloadTemplate}>
                      Download Template
                    </Button>

                    <Button variant="outlined" component="label" sx={{ minWidth: 140 }}>
                      {file ? "Change File" : "Select File"}
                      <input type="file" hidden onChange={handleFileChange} />
                    </Button>

                    <Button variant="contained" color="success" onClick={handlePreview}>
                      Preview
                    </Button>
                  </Box>

                  {file && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Selected File: <strong>{file.name}</strong>
                    </Typography>
                  )}

                  {previewData.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography fontWeight="500">Preview Data:</Typography>
                      <pre style={{ fontSize: "12px", marginTop: "8px" }}>
                        {JSON.stringify(previewData, null, 2)}
                      </pre>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleUpload}
                        sx={{ mt: 1 }}
                      >
                        Confirm & Upload
                      </Button>
                    </Paper>
                  )}

                  {message && (
                    <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                      {message}
                    </Typography>
                  )}

                  <Box textAlign="right" mt={3}>
                    <Button variant="contained" color="error" onClick={handleClose}>
                      Close
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Footer />
    </DashboardLayout>
  );
}
